(function() {
  if (customElements.get('gawx-video-fallback')) {
    return;
  }

  class GawxVideoFallback extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.frameCount = Math.max(parseInt(this.dataset.frameCount, 10) || 145, 1);
      this.fallbackBase = this.dataset.fallbackBase || '';
      this.preferFrames = this.dataset.preferFrames === 'true' && !!this.fallbackBase;
      this.canvas = this.querySelector('.gawx-video-fallback__canvas');
      this.context = this.canvas ? this.canvas.getContext('2d') : null;
      this.poster = this.querySelector('.gawx-video-fallback__poster');
      this.desktopVideo = this.querySelector('.gawx-video-fallback__video--desktop');
      this.mobileVideo = this.querySelector('.gawx-video-fallback__video--mobile');
      this.copyDuration = parseFloat(this.dataset.videoDuration) || 6;
      this.copyItems = Array.from(this.querySelectorAll('.gawx-video-fallback__copy-item')).map((el) => ({
        el: el,
        start: parseFloat(el.dataset.copyStart) || 0,
        end: parseFloat(el.dataset.copyEnd) || this.copyDuration
      }));
      this.checkpointSnap = this.dataset.checkpointSnap === 'true';
      this.mobileAccordion = this.dataset.mobileMode === 'accordion';
      this.images = {};
      this.imagePromises = {};
      this.currentProgress = 0;
      this.currentFrame = 0;
      this.lastRenderedFrame = -1;
      this.hasStarted = false;
      this.renderMode = this.dataset.renderMode || 'poster';
      this.activeVideo = null;
      this.activeVideoFrameReady = false;
      this.videoReadyPromise = null;
      this.scrollTrigger = null;
      this.nativeScrollRaf = null;
      this.nativeScrollHandler = null;
      this.displayProgress = 0;
      this.targetProgress = 0;
      this.videoDamping = 0.24;
      this.videoSeekThreshold = 1 / 120;
      this.videoProgressRaf = null;
      this.videoSeekRaf = null;
      this.videoSeekDelayTimeout = null;
      this.pendingVideoTime = null;
      this.lastVideoSeekAt = 0;
      this.videoSeekTimeout = null;
      this.framePreloadRadius = 4;
      this.startCheckTimeouts = [];
      this.resizeHandler = this.resizeCanvas.bind(this);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoQuery = window.matchMedia('(max-width: 767px)');
      this.mediaChangeHandler = this.handleMediaChange.bind(this);
      this.accordionItems = [];
      this.accordionClickHandler = this.handleAccordionSummaryClick.bind(this);
      this.intersectionObserver = new IntersectionObserver(this.handleIntersect.bind(this), { rootMargin: '3600px 0px' });
      this.intersectionObserver.observe(this);
      this.attachVideoStateListeners();
      this.setupAccordionAnimation();
      window.addEventListener('resize', this.resizeHandler);
      if (this.videoQuery.addEventListener) {
        this.videoQuery.addEventListener('change', this.mediaChangeHandler);
      } else {
        this.videoQuery.addListener(this.mediaChangeHandler);
      }
      this.setRenderMode(this.dataset.renderMode || (this.poster ? 'poster' : this.getFallbackMode()));
      if (this.renderMode === 'video') {
        this.selectActiveVideo();
        this.ensureActiveVideoReady().catch(() => {});
      }
      this.updateVideoFrameReady();
      this.resizeCanvas();
      window.requestAnimationFrame(() => this.startIfNearViewport());
      this.startCheckTimeouts.push(window.setTimeout(() => this.startIfNearViewport(), 1000));
      this.startCheckTimeouts.push(window.setTimeout(() => this.startIfNearViewport(), 2500));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
      if (this.videoQuery.removeEventListener) {
        this.videoQuery.removeEventListener('change', this.mediaChangeHandler);
      } else {
        this.videoQuery.removeListener(this.mediaChangeHandler);
      }
      if (this.scrollTrigger) this.scrollTrigger.kill();
      if (this.nativeScrollHandler) window.removeEventListener('scroll', this.nativeScrollHandler);
      if (this.nativeScrollRaf) window.cancelAnimationFrame(this.nativeScrollRaf);
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.videoProgressRaf) window.cancelAnimationFrame(this.videoProgressRaf);
      if (this.videoSeekRaf) window.cancelAnimationFrame(this.videoSeekRaf);
      if (this.videoSeekDelayTimeout) window.clearTimeout(this.videoSeekDelayTimeout);
      if (this.videoSeekTimeout) window.clearTimeout(this.videoSeekTimeout);
      this.startCheckTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      this.startCheckTimeouts = [];
      this.teardownAccordionAnimation();
    }

    setupAccordionAnimation() {
      this.accordionItems = Array.from(this.querySelectorAll('.gawx-video-fallback__item'));
      if (!this.accordionItems.length) return;
      this.classList.add('is-accordion-enhanced');
      this.accordionItems.forEach((item) => {
        const summary = item.querySelector('.gawx-video-fallback__summary');
        const panel = item.querySelector('.gawx-video-fallback__panel');
        if (!summary || !panel) return;
        summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
        summary.addEventListener('click', this.accordionClickHandler);
        panel.style.height = item.open ? 'auto' : '0px';
      });
    }

    teardownAccordionAnimation() {
      this.accordionItems.forEach((item) => {
        const summary = item.querySelector('.gawx-video-fallback__summary');
        if (summary) summary.removeEventListener('click', this.accordionClickHandler);
      });
      this.accordionItems = [];
    }

    handleAccordionSummaryClick(event) {
      const summary = event.currentTarget;
      const item = summary.closest('.gawx-video-fallback__item');
      if (!item) return;
      if (item.dataset.animating === 'true') {
        event.preventDefault();
        return;
      }
      const panel = item.querySelector('.gawx-video-fallback__panel');
      if (!panel) return;
      event.preventDefault();
      if (this.motionQuery.matches) {
        item.open = !item.open;
        panel.style.height = item.open ? 'auto' : '0px';
        summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
        this.refreshScrollTriggers();
        return;
      }
      if (item.open) {
        this.closeAccordionItem(item, panel, summary);
        return;
      }
      this.openAccordionItem(item, panel, summary);
    }

    openAccordionItem(item, panel, summary) {
      item.dataset.animating = 'true';
      delete item.dataset.closing;
      item.open = true;
      summary.setAttribute('aria-expanded', 'true');
      panel.style.height = '0px';
      window.requestAnimationFrame(() => {
        panel.style.height = panel.scrollHeight + 'px';
      });
      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        panel.style.height = 'auto';
        delete item.dataset.animating;
        this.refreshScrollTriggers();
      };
      panel.addEventListener('transitionend', onTransitionEnd);
    }

    closeAccordionItem(item, panel, summary) {
      item.dataset.animating = 'true';
      item.dataset.closing = 'true';
      summary.setAttribute('aria-expanded', 'false');
      panel.style.height = panel.scrollHeight + 'px';
      panel.offsetHeight;
      window.requestAnimationFrame(() => {
        panel.style.height = '0px';
      });
      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        item.open = false;
        delete item.dataset.animating;
        delete item.dataset.closing;
        this.refreshScrollTriggers();
      };
      panel.addEventListener('transitionend', onTransitionEnd);
    }

    refreshScrollTriggers() {
      if (typeof window.ScrollTrigger !== 'undefined') {
        window.ScrollTrigger.refresh();
      }
    }

    attachVideoStateListeners() {
      [this.desktopVideo, this.mobileVideo].forEach((video) => {
        if (!video) return;
        ['loadeddata', 'canplay', 'playing', 'timeupdate', 'seeked'].forEach((eventName) => {
          video.addEventListener(eventName, () => this.updateVideoFrameReady(video));
        });
      });
    }

    handleIntersect(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.startMediaDriver();
    }

    startIfNearViewport() {
      if (this.hasStarted) return;
      const bounds = this.getBoundingClientRect();
      if (bounds.bottom < -3600 || bounds.top > window.innerHeight + 3600) return;
      this.startMediaDriver();
    }

    startMediaDriver() {
      if (this.hasStarted) return;
      if (this.mobileAccordion && this.videoQuery.matches) return;
      this.hasStarted = true;
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      if (this.motionQuery.matches) {
        this.activateFrameFallback();
        return;
      }
      this.waitForGsap().then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        if (this.preferFrames) {
          this.setRenderMode('frames');
          this.ensureFrameLoaded(0).catch(() => {});
          this.renderProgress(this.getCurrentScrollProgress(), false);
          this.createScrollTrigger();
          return;
        }
        this.selectActiveVideo();
        this.primeMedia().then((mode) => {
          this.setRenderMode(mode);
          this.primeRenderProgress();
          this.createScrollTrigger();
        });
      }).catch(() => {
        this.setRenderMode(this.activateFallbackMode());
        this.createNativeScrollDriver();
      });
    }

    waitForGsap() {
      return new Promise((resolve, reject) => {
        const startedAt = performance.now();
        const check = () => {
          if (typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') {
            resolve();
            return;
          }
          if (performance.now() - startedAt > 6000) {
            reject(new Error('GSAP timeout'));
            return;
          }
          window.requestAnimationFrame(check);
        };
        check();
      });
    }

    handleMediaChange() {
      if (this.mobileAccordion && !this.videoQuery.matches && !this.hasStarted) {
        this.startIfNearViewport();
        return;
      }
      this.selectActiveVideo();
      this.videoReadyPromise = null;
      if (this.renderMode === 'video') {
        this.ensureActiveVideoReady()
          .then(() => this.syncVideoToProgress(this.currentProgress))
          .catch(() => this.setRenderMode(this.getFallbackMode()));
        return;
      }
      this.renderFrame(this.currentFrame);
    }

    selectActiveVideo() {
      const isMobile = this.videoQuery.matches;
      this.activeVideo = isMobile ? (this.mobileVideo || this.desktopVideo) : (this.desktopVideo || this.mobileVideo);
      [this.desktopVideo, this.mobileVideo].forEach((video) => {
        if (!video) return;
        video.classList.toggle('is-active', video === this.activeVideo);
      });
      this.activeVideoFrameReady = !!this.activeVideo && this.activeVideo.readyState >= 2;
      this.updateVideoFrameReady();
    }

    updateVideoFrameReady(video = this.activeVideo) {
      if (video && video === this.activeVideo && video.readyState >= 2) {
        this.activeVideoFrameReady = true;
      }
      this.dataset.videoFrameReady = this.activeVideoFrameReady ? 'true' : 'false';
      if (this.activeVideoFrameReady && this.pendingVideoTime !== null && video && Number.isFinite(video.duration) && video.duration > 0) {
        const pendingTime = this.pendingVideoTime;
        this.pendingVideoTime = null;
        this.syncVideoToProgress(pendingTime / video.duration);
      }
    }

    ensureActiveVideoReady() {
      if (!this.activeVideo) return Promise.reject(new Error('Missing active video'));
      if (!this.videoReadyPromise) {
        this.videoReadyPromise = this.prepareVideo(this.activeVideo).catch((error) => {
          this.videoReadyPromise = null;
          throw error;
        });
      }
      return this.videoReadyPromise;
    }

    primeMedia() {
      if (!this.activeVideo) {
        return Promise.resolve(this.activateFallbackMode());
      }
      return this.ensureActiveVideoReady()
        .then(() => 'video')
        .catch(() => 'video');
    }

    prepareVideo(video) {
      if (!video) return Promise.reject(new Error('Missing video'));
      return new Promise((resolve, reject) => {
        let settled = false;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        const cleanup = () => {
          window.clearTimeout(timeoutId);
          video.removeEventListener('loadedmetadata', onReady);
          video.removeEventListener('loadeddata', onFrameReady);
          video.removeEventListener('canplay', onFrameReady);
          video.removeEventListener('playing', onFrameReady);
          video.removeEventListener('timeupdate', onFrameReady);
          video.removeEventListener('error', onError);
        };
        const finish = (callback) => {
          if (settled) return;
          settled = true;
          cleanup();
          callback();
        };
        const onReady = () => {
          try {
            video.pause();
          } catch (error) {}
          if (video.readyState >= 2) {
            onFrameReady();
            return;
          }
          video.addEventListener('loadeddata', onFrameReady);
          video.addEventListener('canplay', onFrameReady);
          video.addEventListener('playing', onFrameReady);
          video.addEventListener('timeupdate', onFrameReady);
          const warmup = video.play();
          if (warmup && typeof warmup.then === 'function') {
            warmup.catch(() => {});
          }
        };
        const onFrameReady = () => {
          const warmup = video.play();
          if (warmup && typeof warmup.then === 'function') {
            warmup.then(() => {
              video.pause();
              this.updateVideoFrameReady(video);
              finish(resolve);
            }).catch(() => {
              finish(resolve);
            });
            return;
          }
          this.updateVideoFrameReady(video);
          finish(resolve);
        };
        const onError = () => {
          finish(reject);
        };
        const timeoutId = window.setTimeout(() => finish(reject), 6500);
        if (video.readyState >= 2 && isFinite(video.duration) && video.duration > 0) {
          onFrameReady();
          return;
        }
        if (video.readyState >= 1 && isFinite(video.duration) && video.duration > 0) {
          onReady();
          return;
        }
        video.addEventListener('loadedmetadata', onReady);
        video.addEventListener('error', onError);
        video.load();
      });
    }

    activateFrameFallback() {
      this.ensureFrameLoaded(0)
        .then(() => this.renderFrame(this.currentFrame))
        .catch(() => this.setRenderMode(this.poster ? 'poster' : 'frames'));
      return 'frames';
    }

    activateFallbackMode() {
      const fallbackMode = this.getFallbackMode();
      if (fallbackMode === 'frames') {
        this.activateFrameFallback();
        return 'frames';
      }
      this.setRenderMode(fallbackMode);
      return fallbackMode;
    }

    getFallbackMode() {
      if (this.fallbackBase) return 'frames';
      if (this.poster) return 'poster';
      return 'frames';
    }

    getCheckpointProgressList() {
      if (!this.checkpointSnap || !this.copyItems || !this.copyItems.length) return [];
      const duration = this.copyDuration > 0 ? this.copyDuration : 6;
      const values = new Set();
      this.copyItems.forEach((item) => {
        const mid = (item.start + item.end) / 2;
        const progress = Math.max(0, Math.min(1, mid / duration));
        values.add(Math.round(progress * 1000) / 1000);
      });
      if (!values.size) return [];
      values.add(0);
      values.add(1);
      return Array.from(values).sort((a, b) => a - b);
    }

    createScrollTrigger() {
      if (this.scrollTrigger) this.scrollTrigger.kill();
      const config = {
        trigger: this,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.45,
        onUpdate: (self) => this.updateByProgress(self.progress)
      };
      const checkpoints = this.getCheckpointProgressList();
      if (checkpoints.length > 1) {
        config.snap = {
          snapTo: checkpoints,
          duration: { min: 0.25, max: 0.9 },
          delay: 0.08,
          ease: 'power2.inOut',
          directional: true
        };
      }
      this.scrollTrigger = window.ScrollTrigger.create(config);
      window.ScrollTrigger.refresh();
    }

    createNativeScrollDriver() {
      if (this.nativeScrollHandler) window.removeEventListener('scroll', this.nativeScrollHandler);
      this.nativeScrollHandler = () => {
        if (this.nativeScrollRaf) return;
        this.nativeScrollRaf = window.requestAnimationFrame(() => {
          this.nativeScrollRaf = null;
          this.updateByProgress(this.getCurrentScrollProgress());
        });
      };
      window.addEventListener('scroll', this.nativeScrollHandler, { passive: true });
      this.nativeScrollHandler();
    }

    updateByProgress(progress) {
      this.currentProgress = progress;
      if (this.renderMode === 'video') {
        this.updateVideoProgress(progress);
        return;
      }
      this.renderProgress(progress, false);
    }

    updateVideoProgress(progress) {
      this.targetProgress = progress;
      if (!this.videoProgressRaf) this.stepVideoProgress();
    }

    stepVideoProgress() {
      const delta = this.targetProgress - this.displayProgress;
      if (Math.abs(delta) < 0.001) {
        this.displayProgress = this.targetProgress;
        this.videoProgressRaf = null;
        this.renderProgress(this.displayProgress, true);
        return;
      }
      this.displayProgress += delta * this.videoDamping;
      this.renderProgress(this.displayProgress, true);
      this.videoProgressRaf = window.requestAnimationFrame(() => {
        this.videoProgressRaf = null;
        this.stepVideoProgress();
      });
    }

    renderProgress(progress, shouldSyncVideo) {
      this.updateCopyVisibility(progress);
      this.currentFrame = Math.min(this.frameCount - 1, Math.round(progress * (this.frameCount - 1)));
      if (shouldSyncVideo) {
        this.syncVideoToProgress(progress);
        return;
      }
      if (this.renderMode === 'frames') {
        this.renderBlendedFrame(progress);
      }
    }

    setRenderMode(mode) {
      this.renderMode = mode;
      this.dataset.renderMode = mode;
    }

    primeRenderProgress() {
      if (this.renderMode !== 'video') return;
      const progress = this.getCurrentScrollProgress();
      this.currentProgress = progress;
      this.targetProgress = progress;
      this.displayProgress = progress;
      this.renderProgress(progress, true);
    }

    getCurrentScrollProgress() {
      const scrollable = Math.max(this.offsetHeight - window.innerHeight, 1);
      return Math.max(0, Math.min(1, (window.scrollY - this.offsetTop) / scrollable));
    }

    syncVideoToProgress(progress) {
      if (!this.activeVideo || !isFinite(this.activeVideo.duration) || this.activeVideo.duration <= 0) {
        return;
      }
      const safeDuration = Math.max(this.activeVideo.duration - 0.04, 0);
      const targetTime = Math.max(0, Math.min(progress * this.activeVideo.duration, safeDuration));
      if (Math.abs(this.activeVideo.currentTime - targetTime) < this.videoSeekThreshold) {
        return;
      }
      this.pendingVideoTime = targetTime;
      if (this.activeVideo.readyState < 2) {
        this.ensureActiveVideoReady().then(() => {
          if (this.pendingVideoTime !== null && this.activeVideo && this.activeVideo.readyState >= 2) {
            const pendingTime = this.pendingVideoTime;
            this.pendingVideoTime = null;
            this.syncVideoToProgress(pendingTime / this.activeVideo.duration);
          }
        }).catch(() => {});
        return;
      }
      const now = performance.now();
      const seekInterval = this.getVideoSeekInterval();
      const remainingDelay = seekInterval - (now - this.lastVideoSeekAt);
      if (remainingDelay > 0) {
        if (!this.videoSeekDelayTimeout) {
          this.videoSeekDelayTimeout = window.setTimeout(() => {
            this.videoSeekDelayTimeout = null;
            this.applyPendingVideoSeek();
          }, remainingDelay);
        }
        return;
      }
      this.applyPendingVideoSeek();
    }

    getVideoSeekInterval() {
      return this.videoQuery.matches ? 110 : 16;
    }

    applyPendingVideoSeek() {
      const video = this.activeVideo;
      if (!video || this.pendingVideoTime === null) return;
      const now = performance.now();
      const seekInterval = this.getVideoSeekInterval();
      const remainingDelay = seekInterval - (now - this.lastVideoSeekAt);
      if (remainingDelay > 0) {
        if (!this.videoSeekDelayTimeout) {
          this.videoSeekDelayTimeout = window.setTimeout(() => {
            this.videoSeekDelayTimeout = null;
            this.applyPendingVideoSeek();
          }, remainingDelay);
        }
        return;
      }
      if (video.seeking && now - this.lastVideoSeekAt < 180) {
        this.videoSeekRaf = window.requestAnimationFrame(() => {
          this.videoSeekRaf = null;
          this.applyPendingVideoSeek();
        });
        return;
      }
      const targetTime = this.pendingVideoTime;
      this.pendingVideoTime = null;
      this.lastVideoSeekAt = now;
      try {
        video.currentTime = targetTime;
      } catch (error) {
        this.fallbackAfterVideoFailure();
        return;
      }
      if (this.videoSeekTimeout) window.clearTimeout(this.videoSeekTimeout);
      this.videoSeekTimeout = window.setTimeout(() => {
        if (!video.seeking || this.pendingVideoTime !== null) return;
        this.pendingVideoTime = targetTime;
        if (this.videoSeekRaf) return;
        this.videoSeekRaf = window.requestAnimationFrame(() => {
          this.videoSeekRaf = null;
          this.applyPendingVideoSeek();
        });
      }, 1200);
    }

    fallbackAfterVideoFailure() {
      if (this.videoSeekRaf) {
        window.cancelAnimationFrame(this.videoSeekRaf);
        this.videoSeekRaf = null;
      }
      if (this.videoSeekDelayTimeout) {
        window.clearTimeout(this.videoSeekDelayTimeout);
        this.videoSeekDelayTimeout = null;
      }
      if (this.videoSeekTimeout) {
        window.clearTimeout(this.videoSeekTimeout);
        this.videoSeekTimeout = null;
      }
      this.pendingVideoTime = null;
      if (this.activeVideo) {
        this.setRenderMode('video');
        try {
          this.activeVideo.load();
        } catch (error) {}
        this.syncVideoToProgress(this.currentProgress);
        return;
      }
      this.setRenderMode(this.activateFallbackMode());
      this.renderFrame(this.currentFrame);
    }

    updateCopyVisibility(progress) {
      if (!this.copyItems || !this.copyItems.length) return;
      const duration = this.copyDuration > 0 ? this.copyDuration : 6;
      const time = Math.max(0, Math.min(1, progress)) * duration;
      this.copyItems.forEach((item) => {
        const visible = time >= item.start && time <= item.end;
        item.el.classList.toggle('is-visible', visible);
      });
    }

    resizeCanvas() {
      if (!this.canvas || !this.context) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvasWidth = window.innerWidth;
      this.canvasHeight = window.innerHeight;
      this.canvas.width = Math.floor(this.canvasWidth * ratio);
      this.canvas.height = Math.floor(this.canvasHeight * ratio);
      this.context.setTransform(1, 0, 0, 1, 0, 0);
      this.context.scale(ratio, ratio);
      if (this.context) {
        this.context.imageSmoothingEnabled = true;
        this.context.imageSmoothingQuality = 'high';
      }
      if (this.renderMode === 'frames') this.renderBlendedFrame(this.currentProgress);
    }

    renderFrame(frameIndex) {
      if (!this.context) return;
      const image = this.images[frameIndex];
      if (!image) {
        this.ensureFrameLoaded(frameIndex).then(() => {
          if (frameIndex === this.currentFrame && this.renderMode === 'frames') {
            this.renderFrame(frameIndex);
          }
        }).catch(() => this.renderNearestLoadedFrame(frameIndex));
        this.preloadNearbyFrames(frameIndex);
        this.renderNearestLoadedFrame(frameIndex);
        return;
      }
      this.drawMedia(image.naturalWidth, image.naturalHeight, (dx, dy, dw, dh) => {
        this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.context.drawImage(image, dx, dy, dw, dh);
      });
      this.lastRenderedFrame = frameIndex;
      this.preloadNearbyFrames(frameIndex);
    }

    renderBlendedFrame(progress) {
      if (!this.context) return;
      const exact = Math.max(0, Math.min(this.frameCount - 1, progress * (this.frameCount - 1)));
      const lowFrame = Math.floor(exact);
      const highFrame = Math.min(this.frameCount - 1, lowFrame + 1);
      const blend = exact - lowFrame;
      const lowImage = this.images[lowFrame];
      const highImage = this.images[highFrame];
      this.currentFrame = lowFrame;
      if (!lowImage) {
        this.renderFrame(lowFrame);
        return;
      }
      this.drawMedia(lowImage.naturalWidth, lowImage.naturalHeight, (dx, dy, dw, dh) => {
        this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.context.globalAlpha = 1;
        this.context.drawImage(lowImage, dx, dy, dw, dh);
      });
      if (highImage && highFrame !== lowFrame && blend > 0.01) {
        this.drawMedia(highImage.naturalWidth, highImage.naturalHeight, (hx, hy, hw, hh) => {
          this.context.globalAlpha = blend;
          this.context.drawImage(highImage, hx, hy, hw, hh);
          this.context.globalAlpha = 1;
        });
      }
      this.lastRenderedFrame = lowFrame;
      this.preloadNearbyFrames(lowFrame);
    }

    renderNearestLoadedFrame(frameIndex) {
      if (!this.context) return;
      for (let offset = 1; offset <= this.framePreloadRadius; offset++) {
        const previousFrame = frameIndex - offset;
        const nextFrame = frameIndex + offset;
        if (previousFrame >= 0 && this.images[previousFrame]) {
          this.renderFrame(previousFrame);
          return;
        }
        if (nextFrame < this.frameCount && this.images[nextFrame]) {
          this.renderFrame(nextFrame);
          return;
        }
      }
    }

    preloadNearbyFrames(frameIndex) {
      for (let offset = 1; offset <= this.framePreloadRadius; offset++) {
        const previousFrame = frameIndex - offset;
        const nextFrame = frameIndex + offset;
        if (previousFrame >= 0) this.ensureFrameLoaded(previousFrame).catch(() => {});
        if (nextFrame < this.frameCount) this.ensureFrameLoaded(nextFrame).catch(() => {});
      }
    }

    ensureFrameLoaded(frameIndex) {
      if (!this.fallbackBase) return Promise.reject(new Error('Missing fallback base'));
      if (this.images[frameIndex]) return Promise.resolve(this.images[frameIndex]);
      if (this.imagePromises[frameIndex]) return this.imagePromises[frameIndex];
      this.imagePromises[frameIndex] = new Promise((resolve, reject) => {
        const image = new Image();
        image.decoding = 'async';
        image.onload = () => {
          this.images[frameIndex] = image;
          resolve(image);
        };
        image.onerror = () => {
          delete this.imagePromises[frameIndex];
          reject(new Error('Frame failed to load'));
        };
        image.src = this.getFrameUrl(frameIndex);
      });
      return this.imagePromises[frameIndex];
    }

    getFrameUrl(frameIndex) {
      return this.fallbackBase + String(frameIndex).padStart(5, '0') + '.webp';
    }

    drawMedia(mediaWidth, mediaHeight, callback) {
      if (!mediaWidth || !mediaHeight) return;
      const scale = this.canvasWidth / mediaWidth;
      const drawWidth = mediaWidth * scale;
      const drawHeight = mediaHeight * scale;
      const drawX = (this.canvasWidth - drawWidth) / 2;
      const drawY = (this.canvasHeight - drawHeight) / 2;
      callback(drawX, drawY, drawWidth, drawHeight);
    }
  }

  customElements.define('gawx-video-fallback', GawxVideoFallback);
})();
